import { AnimatePresence, motion } from 'framer-motion';
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react';
import { useToastStore, type ToastKind } from '@/store/toastStore';
import { cn } from '@/utils/cn';

const icons: Record<ToastKind, typeof Info> = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  error: CircleAlert,
};

const accents: Record<ToastKind, string> = {
  info: 'text-ink-400',
  success: 'text-good-500',
  warning: 'text-signal-500',
  error: 'text-bad-500',
};

export function Toaster() {
  const toasts = useToastStore((state) => state.toasts);
  const dismiss = useToastStore((state) => state.dismiss);

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4 sm:items-end sm:pr-6"
    >
      <AnimatePresence initial={false}>
        {toasts.map((item) => {
          const Icon = icons[item.kind];
          return (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={{ duration: 0.18 }}
              role={item.kind === 'error' ? 'alert' : 'status'}
              className="panel pointer-events-auto flex w-full max-w-sm items-start gap-3 p-3"
            >
              <Icon aria-hidden className={cn('mt-0.5 size-4 shrink-0', accents[item.kind])} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{item.title}</p>
                {item.message && (
                  <p className="mt-0.5 text-xs break-words text-ink-500 dark:text-ink-400">
                    {item.message}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => dismiss(item.id)}
                aria-label="Dismiss notification"
                className="-m-1 rounded p-1 text-ink-400 hover:text-ink-700 dark:hover:text-ink-200"
              >
                <X aria-hidden className="size-4" />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
